import { styled } from "goober";
import { SKILL_MD_PATH } from "../../../shared/skills/spec";
import { Dropdown } from "../Dropdown";
import { FileChangeDot } from "../FileChangeDot";
import { Icon } from "../Icon";
import { folderChangeIndicator, type MergedFile } from "../stepFilesDiff";
import { Text } from "../Text";

const HeadingOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 9999px;
  background: var(--alpha4);

  &:hover {
    background: var(--alpha8);
  }
`;

const OpenerDivider = styled("div")`
  width: 0.5px;
  height: 100%;
  background: var(--border-alpha);
  flex-shrink: 0;
`;

type File = {
  path: string;
  kind?: MergedFile["kind"];
};

export function SkillFilePicker({
  files,
  selectedPath,
  onSelectPath,
  selectedBinary = false,
}: {
  files: readonly File[];
  selectedPath: string;
  onSelectPath: (path: string) => void;
  selectedBinary?: boolean;
}) {
  const fileButton = (file: File, label: string) => ({
    type: "button" as const,
    key: file.path,
    label: (
      <>
        {label}
        {file.kind && file.kind !== "unchanged" && (
          <FileChangeDot removed={file.kind === "removed"} />
        )}
      </>
    ),
    left: (
      <Icon
        name={
          file.path === selectedPath && selectedBinary
            ? "package-2"
            : "notebook-text"
        }
        size={16}
      />
    ),
    right:
      file.path === selectedPath ? (
        <Icon name="check" size={12} color="var(--text-secondary)" />
      ) : undefined,
    onClick: () => onSelectPath(file.path),
  });

  const flatFileItems = files
    .filter((file) => !file.path.includes("/"))
    .toSorted((a, b) => {
      if (a.path === SKILL_MD_PATH) return -1;
      if (b.path === SKILL_MD_PATH) return 1;
      return a.path.localeCompare(b.path);
    })
    .map((file) => fileButton(file, file.path));

  const filesByDir = new Map<string, File[]>();
  for (const file of files) {
    if (!file.path.includes("/")) continue;
    const dir = file.path.slice(0, file.path.lastIndexOf("/"));
    const existing = filesByDir.get(dir);
    if (existing) existing.push(file);
    else filesByDir.set(dir, [file]);
  }
  const folderItems = [...filesByDir.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([dir, dirFiles]) => {
      const indicator = folderChangeIndicator(
        dirFiles.map((file) => ({ kind: file.kind ?? "unchanged" }))
      );
      return {
        type: "submenu" as const,
        key: dir,
        label: (
          <>
            {dir}
            {indicator && <FileChangeDot removed={indicator === "removed"} />}
          </>
        ),
        left: <Icon name="folder" size={16} />,
        selected: selectedPath.startsWith(`${dir}/`),
        submenu: [
          {
            items: dirFiles
              .toSorted((a, b) => a.path.localeCompare(b.path))
              .map((file) => fileButton(file, file.path.slice(dir.length + 1))),
          },
        ],
      };
    });

  return (
    <Dropdown
      align="bottom-left"
      opener={
        <HeadingOpener>
          <Text
            variant="text-small-md"
            color="var(--text-primary)"
            style={{ padding: "0 2px" }}
          >
            {files.length}
          </Text>
          <OpenerDivider />
          <span style={{ display: "flex" }}>
            <Icon name="code" size={12} />
          </span>
          <Text variant="text-small-md" ellipsis>
            {selectedPath}
          </Text>
          <Icon name="chevron-down" size={12} />
        </HeadingOpener>
      }
      sections={[{ label: "Files", items: [...flatFileItems, ...folderItems] }]}
    />
  );
}
